import html2canvas from 'html2canvas'

export interface ExportTitleConfig {
    title?: string
    subtitle?: string
    fontFamily?: string
    titleFontSize?: number
    subtitleFontSize?: number
    titleColor?: string
    subtitleColor?: string
}

export interface CaptureOptions {
    fullWidth?: boolean
    scrollContainerSelector?: string
    fullWidthElementSelector?: string
    cssContextClass?: string
    hideSelectors?: string[]
    scale?: number
    backgroundColor?: string
    svgExpandLeft?: number
}

export interface CSVExportConfig {
    headers: string[]
    rows: (string | number | null | undefined)[][]
    filename: string
}

const DEFAULT_FONT_FAMILY = 'Poppins, Segoe UI, sans-serif'
const DEFAULT_TITLE_COLOR = '#000000'
const DEFAULT_SUBTITLE_COLOR = '#000000'
const DEFAULT_BG_COLOR = '#ffffff'
const DEFAULT_SCALE = 2

const DEFAULT_HIDE_SELECTORS = [
    '.export-menu',
    '.export-dropdown',
    '.hamburger',
    '.hamburger-btn',
    '.export-dropdown-menu',
    '.custom-download-menu'
]

export const captureElementDirect = async (
    element: HTMLElement,
    options: CaptureOptions = {}
): Promise<HTMLCanvasElement> => {
    const {
        scale = DEFAULT_SCALE,
        backgroundColor = DEFAULT_BG_COLOR,
        hideSelectors = [],
        cssContextClass,
        svgExpandLeft = 0
    } = options

    const rect = element.getBoundingClientRect()

    const container = document.createElement('div')
    container.style.position = 'absolute'
    container.style.left = '-9999px'
    container.style.top = '0'
    container.style.width = `${Math.ceil(rect.width) + svgExpandLeft}px`
    container.style.background = backgroundColor
    container.style.fontFamily = DEFAULT_FONT_FAMILY
    container.style.pointerEvents = 'none'
    container.style.overflow = 'visible'

    let mountTarget: HTMLElement = container
    if (cssContextClass) {
        const contextWrapper = document.createElement('div')
        contextWrapper.className = `tmb-page ${cssContextClass}`
        contextWrapper.style.width = '100%'
        contextWrapper.style.margin = '0'
        contextWrapper.style.padding = '0'
        contextWrapper.style.background = backgroundColor
        container.appendChild(contextWrapper)
        mountTarget = contextWrapper
    }

    const clone = element.cloneNode(true) as HTMLElement
    clone.style.width = '100%'
    clone.style.maxWidth = '100%'
    clone.style.height = 'auto'
    clone.style.margin = '0'
    clone.style.overflow = 'visible'

    const styleTag = document.createElement('style')
    styleTag.textContent = `
        *, *::before, *::after {
            animation: none !important;
            transition: none !important;
        }
        .bar-fill {
            transform: scaleY(1) !important;
        }
    `
    clone.prepend(styleTag)

    const allHideSelectors = [...DEFAULT_HIDE_SELECTORS, ...hideSelectors]
    allHideSelectors.forEach(selector => {
        clone.querySelectorAll(selector).forEach((el) => {
            (el as HTMLElement).style.display = 'none'
        })
    })

    mountTarget.appendChild(clone)
    document.body.appendChild(container)

    await new Promise(resolve => setTimeout(resolve, 150))

    if (svgExpandLeft > 0) {
        const svgEl = clone.querySelector('.apexcharts-canvas svg') as SVGSVGElement | null
        if (svgEl) {
            const w = svgEl.width.baseVal.value || parseFloat(svgEl.getAttribute('width') || '0')
            const h = svgEl.height.baseVal.value || parseFloat(svgEl.getAttribute('height') || '0')
            if (w > 0 && h > 0) {
                svgEl.setAttribute('width', String(w + svgExpandLeft))
                svgEl.setAttribute('viewBox', `-${svgExpandLeft} 0 ${w + svgExpandLeft} ${h}`)
                svgEl.style.overflow = 'visible'
            }
        }
    }

    try {
        const canvas = await html2canvas(clone, {
            scale,
            useCORS: true,
            backgroundColor,
            logging: false,
            allowTaint: true
        } as any)
        return canvas
    } finally {
        document.body.removeChild(container)
    }
}

export const captureElementOffscreen = async (
    element: HTMLElement,
    options: CaptureOptions = {}
): Promise<HTMLCanvasElement> => {
    const rect = element.getBoundingClientRect()
    const {
        fullWidth = false,
        scrollContainerSelector,
        fullWidthElementSelector,
        cssContextClass,
        hideSelectors = [],
        scale,
        backgroundColor = DEFAULT_BG_COLOR
    } = options

    let fullScrollableWidth: number | null = null
    if (fullWidth && fullWidthElementSelector) {
        const scrollEl = element.querySelector(fullWidthElementSelector) as HTMLElement | null
        if (scrollEl) {
            fullScrollableWidth = Math.ceil(scrollEl.scrollWidth)
        }
    }

    const wrapper = document.createElement('div')
    wrapper.style.position = 'absolute'
    wrapper.style.left = '-9999px'
    wrapper.style.top = '0'
    wrapper.style.width = `${fullScrollableWidth ?? Math.ceil(rect.width)}px`
    wrapper.style.background = backgroundColor
    wrapper.style.fontFamily = DEFAULT_FONT_FAMILY
    wrapper.style.pointerEvents = 'none'

    let contextEl: HTMLElement = wrapper
    if (cssContextClass) {
        const context = document.createElement('div')
        context.className = `tmb-page ${cssContextClass}`
        context.style.width = '100%'
        context.style.margin = '0'
        context.style.padding = '0'
        context.style.background = backgroundColor
        wrapper.appendChild(context)
        contextEl = context
    }

    const clone = element.cloneNode(true) as HTMLElement
    clone.style.width = '100%'
    clone.style.maxWidth = '100%'
    clone.style.height = 'auto'
    clone.style.overflow = 'visible'

    const styleTag = document.createElement('style')
    styleTag.textContent = `
        *, *::before, *::after {
            animation: none !important;
            transition: none !important;
        }
        .bar-fill {
            transform: scaleY(1) !important;
        }
    `
    clone.prepend(styleTag)

    if (fullWidth && fullScrollableWidth) {
        if (scrollContainerSelector) {
            const cloneScroll = clone.querySelector(scrollContainerSelector) as HTMLElement | null
            if (cloneScroll) {
                cloneScroll.style.overflow = 'visible'
                cloneScroll.style.maxWidth = 'none'
                cloneScroll.style.width = `${fullScrollableWidth}px`
            }
        }
        if (fullWidthElementSelector) {
            const cloneEl = clone.querySelector(fullWidthElementSelector) as HTMLElement | null
            if (cloneEl) {
                cloneEl.style.minWidth = `${fullScrollableWidth}px`
                cloneEl.style.width = `${fullScrollableWidth}px`
            }
        }
    }

    const allHideSelectors = [...DEFAULT_HIDE_SELECTORS, ...hideSelectors]
    allHideSelectors.forEach(selector => {
        clone.querySelectorAll(selector).forEach((el) => {
            (el as HTMLElement).style.display = 'none'
        })
    })

    contextEl.appendChild(clone)
    document.body.appendChild(wrapper)

    await new Promise(resolve => setTimeout(resolve, 100))

    try {
        const effectiveScale = scale ??
            (fullScrollableWidth && fullScrollableWidth > 2200 ? 1.5 : DEFAULT_SCALE)

        return await html2canvas(clone, {
            scale: effectiveScale,
            useCORS: true,
            backgroundColor,
            logging: false,
            allowTaint: true
        } as any)
    } finally {
        document.body.removeChild(wrapper)
    }
}

export const exportToPNG = async (
    element: HTMLElement,
    filename: string,
    titleConfig?: ExportTitleConfig,
    captureOptions?: CaptureOptions
): Promise<void> => {
    const canvas = captureOptions?.fullWidth
        ? await captureElementOffscreen(element, captureOptions)
        : await captureElementDirect(element, captureOptions)
    const scale = captureOptions?.scale ?? DEFAULT_SCALE

    const hasTitle = Boolean(titleConfig?.title?.trim())
    const hasSubtitle = Boolean(titleConfig?.subtitle?.trim())

    if (!hasTitle && !hasSubtitle) {
        const link = document.createElement('a')
        link.download = `${filename}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
        return
    }

    const titleFontSize = (titleConfig?.titleFontSize ?? 16) * scale
    const subtitleFontSize = (titleConfig?.subtitleFontSize ?? 13) * scale
    const padding = 10 * scale
    const fontFamily = titleConfig?.fontFamily ?? DEFAULT_FONT_FAMILY
    const maxTextWidth = Math.max(48, canvas.width - 2 * padding)

    const titleFont = `bold ${titleFontSize}px ${fontFamily}`
    const subtitleFont = `bold ${subtitleFontSize}px ${fontFamily}`

    const titleLines = hasTitle ? wrapTextToLines(titleConfig!.title!.trim(), maxTextWidth, titleFont) : []
    const subtitleLines = hasSubtitle ? wrapTextToLines(titleConfig!.subtitle!.trim(), maxTextWidth, subtitleFont) : []

    const titleLineHeight = Math.ceil(titleFontSize * 1.22)
    const subtitleLineHeight = Math.ceil(subtitleFontSize * 1.22)

    let titleBlockHeight = padding
    if (titleLines.length) titleBlockHeight += titleFontSize + (titleLines.length - 1) * titleLineHeight
    if (titleLines.length && subtitleLines.length) titleBlockHeight += padding * 0.6
    if (subtitleLines.length) titleBlockHeight += subtitleFontSize + (subtitleLines.length - 1) * subtitleLineHeight
    titleBlockHeight += padding

    const finalCanvas = document.createElement('canvas')
    finalCanvas.width = canvas.width
    finalCanvas.height = canvas.height + titleBlockHeight
    const ctx = finalCanvas.getContext('2d')
    
    if (!ctx) {
        console.error('Failed to get canvas context')
        return
    }

    ctx.fillStyle = captureOptions?.backgroundColor ?? DEFAULT_BG_COLOR
    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height)

    ctx.textAlign = 'center'
    const cx = finalCanvas.width / 2
    let baseline = padding + titleFontSize

    if (titleLines.length) {
        ctx.font = titleFont
        ctx.fillStyle = titleConfig?.titleColor ?? DEFAULT_TITLE_COLOR
        for (const line of titleLines) {
            ctx.fillText(line, cx, baseline)
            baseline += titleLineHeight
        }
    }

    if (subtitleLines.length) {
        if (titleLines.length) baseline += padding * 0.35
        else baseline = padding + subtitleFontSize
        ctx.font = subtitleFont
        ctx.fillStyle = titleConfig?.subtitleColor ?? DEFAULT_SUBTITLE_COLOR
        for (const line of subtitleLines) {
            ctx.fillText(line, cx, baseline)
            baseline += subtitleLineHeight
        }
    }

    ctx.drawImage(canvas, 0, titleBlockHeight)

    const link = document.createElement('a')
    link.download = `${filename}.png`
    link.href = finalCanvas.toDataURL('image/png')
    link.click()
}

export const exportToSVG = async (
    element: HTMLElement,
    filename: string,
    titleConfig?: ExportTitleConfig,
    captureOptions?: CaptureOptions
): Promise<void> => {
    const canvas = captureOptions?.fullWidth
        ? await captureElementOffscreen(element, captureOptions)
        : await captureElementDirect(element, captureOptions)
    const scale = captureOptions?.scale ?? DEFAULT_SCALE

    const width = canvas.width / scale
    const height = canvas.height / scale
    const dataURL = canvas.toDataURL('image/png')

    const hasTitle = Boolean(titleConfig?.title?.trim())
    const hasSubtitle = Boolean(titleConfig?.subtitle?.trim())

    const titleFontSize = titleConfig?.titleFontSize ?? 16
    const subtitleFontSize = titleConfig?.subtitleFontSize ?? 13
    const fontFamily = titleConfig?.fontFamily ?? DEFAULT_FONT_FAMILY
    const titleColor = titleConfig?.titleColor ?? DEFAULT_TITLE_COLOR
    const subtitleColor = titleConfig?.subtitleColor ?? DEFAULT_SUBTITLE_COLOR

    const padTop = 10
    const padBottom = 10
    const maxTextWidth = Math.max(48, width - 20)
    const titleFontCss = `bold ${titleFontSize}px ${fontFamily}`
    const subtitleFontCss = `bold ${subtitleFontSize}px ${fontFamily}`

    const titleLines = hasTitle ? wrapTextToLines(titleConfig!.title!.trim(), maxTextWidth, titleFontCss) : []
    const subtitleLines = hasSubtitle ? wrapTextToLines(titleConfig!.subtitle!.trim(), maxTextWidth, subtitleFontCss) : []

    const titleLineHeight = Math.ceil(titleFontSize * 1.22)
    const subtitleLineHeight = Math.ceil(subtitleFontSize * 1.22)

    let titleBlockHeight = 0
    if (titleLines.length || subtitleLines.length) {
        titleBlockHeight = padTop
        if (titleLines.length) titleBlockHeight += titleFontSize + (titleLines.length - 1) * titleLineHeight
        if (titleLines.length && subtitleLines.length) titleBlockHeight += 6
        if (subtitleLines.length) titleBlockHeight += subtitleFontSize + (subtitleLines.length - 1) * subtitleLineHeight
        titleBlockHeight += padBottom
    }

    const xc = width / 2
    let yBaseline = padTop + titleFontSize
    let titleBlock = ''
    for (const line of titleLines) {
        titleBlock += `  <text x="${xc}" y="${yBaseline}" font-family="${escapeXml(fontFamily)}" font-size="${titleFontSize}" font-weight="bold" text-anchor="middle" fill="${titleColor}">${escapeXml(line)}</text>\n`
        yBaseline += titleLineHeight
    }
    if (titleLines.length && subtitleLines.length) yBaseline += 4
    else if (!titleLines.length && subtitleLines.length) yBaseline = padTop + subtitleFontSize
    for (const line of subtitleLines) {
        titleBlock += `  <text x="${xc}" y="${yBaseline}" font-family="${escapeXml(fontFamily)}" font-size="${subtitleFontSize}" font-weight="bold" text-anchor="middle" fill="${subtitleColor}">${escapeXml(line)}</text>\n`
        yBaseline += subtitleLineHeight
    }

    const svgHeight = height + titleBlockHeight
    const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${svgHeight}">
  <rect width="100%" height="100%" fill="white"/>
${titleBlock}  <image x="0" y="${titleBlockHeight}" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`

    const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.svg`
    link.click()
    URL.revokeObjectURL(url)
}

export const exportToCSV = (config: CSVExportConfig): void => {
    const { headers, rows, filename } = config
    
    const csvLines: string[] = []
    csvLines.push(headers.map(h => escapeCSVField(String(h))).join(','))
    rows.forEach(row => {
        const rowStr = row.map(cell => {
            if (cell === null || cell === undefined) return ''
            return escapeCSVField(String(cell))
        }).join(',')
        csvLines.push(rowStr)
    })

    const csvContent = csvLines.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

const escapeXml = (str: string): string => {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
}

let measureCanvas: HTMLCanvasElement | null = null
const getMeasureCtx = (): CanvasRenderingContext2D | null => {
    if (!measureCanvas) measureCanvas = document.createElement('canvas')
    return measureCanvas.getContext('2d')
}

const wrapTextToLines = (text: string, maxWidth: number, font: string): string[] => {
    const ctx = getMeasureCtx()
    if (!ctx || maxWidth <= 0) return [text]

    ctx.font = font
    const measure = (s: string) => ctx.measureText(s).width

    const breakLongWord = (word: string): string[] => {
        if (measure(word) <= maxWidth) return [word]
        const parts: string[] = []
        let chunk = ''
        for (let i = 0; i < word.length; i++) {
            const next = chunk + word[i]
            if (measure(next) <= maxWidth) {
                chunk = next
            } else {
                if (chunk) parts.push(chunk)
                chunk = word[i]
            }
        }
        if (chunk) parts.push(chunk)
        return parts.length ? parts : [word]
    }

    const words = text.trim().split(/\s+/).filter(Boolean)
    const lines: string[] = []
    let line = ''

    const pushLine = () => {
        if (line) lines.push(line)
        line = ''
    }

    for (const word of words) {
        const candidate = line ? `${line} ${word}` : word
        if (measure(candidate) <= maxWidth) {
            line = candidate
            continue
        }
        if (line) pushLine()
        const segments = breakLongWord(word)
        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i]
            const cand = line ? `${line} ${seg}` : seg
            if (measure(cand) <= maxWidth) {
                line = cand
            } else {
                if (line) pushLine()
                line = seg
            }
        }
    }
    pushLine()
    return lines.length ? lines : ['']
}

const escapeCSVField = (field: string): string => {
    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
        return `"${field.replace(/"/g, '""')}"`
    }
    return field
}
